import { useEffect, useRef, useState } from 'react';

import AcademiaSelect from '../common/AcademiaSelect';
import ApprenantFormField from './ApprenantFormField';
import { ApprenantPaginatedList, ApprenantPaginatedTable } from './ApprenantPaginatedSection';
import ApprenantSectionCard from './ApprenantSectionCard';
import {
  downloadReleveNotesPdf,
  fetchFormateursToEvaluate,
  fetchInscriptionAcademic,
  submitDepotTravail,
  submitEvaluationFormateur,
  uploadDepotTravailFile,
} from '../../services/academiaApprenantService';

type Props = {
  inscriptionId: string;
  onToast?: (message: string, variant: 'success' | 'error') => void;
};

export default function ApprenantInscriptionAcademicSection({ inscriptionId, onToast }: Props) {
  const [data, setData] = useState<{
    stats?: {
      moyenneSur20: number | null;
      moyennePratiqueSur20: number | null;
      tauxPresencePct: number | null;
      nombreNotes: number;
    };
    cohorteVerrouillee?: boolean;
    notes?: {
      typeLabel: string;
      libelle: string;
      noteSur20: number;
      dateEvaluation: string;
      statutDepot?: string | null;
      urlFichier?: string | null;
    }[];
    presences?: { statut: string; dateSeance: string; sessionLabel: string }[];
    scoresExcellence?: { periode: string; rang: number | null; scoreGlobal: number; estShortlist: boolean }[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [formateurs, setFormateurs] = useState<
    {
      idProfilFormateur: string;
      nomComplet: string;
      specialite: string;
      dejaEvalue: boolean;
    }[]
  >([]);
  const [evalForm, setEvalForm] = useState({
    idProfilFormateur: '',
    notePonctualite: '5',
    notePedagogie: '5',
    commentaire: '',
  });
  const [depotForm, setDepotForm] = useState({ typeEvaluation: 'TP', libelle: 'TP 1', urlFichier: '' });
  const [uploadingDepot, setUploadingDepot] = useState(false);
  const [downloadingReleve, setDownloadingReleve] = useState(false);
  const [submittingEval, setSubmittingEval] = useState(false);
  const [submittingDepot, setSubmittingDepot] = useState(false);

  const toast = (message: string, variant: 'success' | 'error') => {
    onToast?.(message, variant);
  };
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = await fetchInscriptionAcademic(inscriptionId);
        if (!cancelled) setData(row);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inscriptionId]);

  useEffect(() => {
    void fetchFormateursToEvaluate(inscriptionId).then(setFormateurs);
  }, [inscriptionId]);

  if (loading) {
    return <p className="text-sm text-on-surface-variant">Chargement du suivi académique…</p>;
  }

  if (!data) {
    return <p className="text-sm text-on-surface-variant">Suivi académique indisponible.</p>;
  }

  const stats = data.stats;

  return (
    <div className="space-y-4">
      <ApprenantSectionCard
        icon="school"
        title="Suivi académique"
        subtitle="Moyennes, notes et présences"
        action={
          <button
            type="button"
            disabled={downloadingReleve}
            className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-bold text-primary disabled:opacity-50"
            onClick={async () => {
              setDownloadingReleve(true);
              try {
                await downloadReleveNotesPdf(inscriptionId);
                toast('Relevé de notes téléchargé avec succès.', 'success');
              } catch (e) {
                toast(e instanceof Error ? e.message : 'Téléchargement du relevé impossible.', 'error');
              } finally {
                setDownloadingReleve(false);
              }
            }}
          >
            {downloadingReleve ? 'Téléchargement…' : 'Relevé PDF'}
          </button>
        }
      >
        {data.cohorteVerrouillee ? (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            La cohorte a été délibérée : les notes et dépôts ne peuvent plus être modifiés.
          </p>
        ) : null}

        <div className="apprenant-inscription-stat-grid">
          <div className="apprenant-inscription-stat">
            <p className="apprenant-inscription-stat__label">Moyenne générale</p>
            <p className="apprenant-inscription-stat__value">
              {stats?.moyenneSur20 != null ? `${stats.moyenneSur20.toFixed(2)}/20` : '—'}
            </p>
          </div>
          <div className="apprenant-inscription-stat">
            <p className="apprenant-inscription-stat__label">TP / Soutenance / Projet</p>
            <p className="apprenant-inscription-stat__value apprenant-inscription-stat__value--neutral">
              {stats?.moyennePratiqueSur20 != null ? `${stats.moyennePratiqueSur20.toFixed(2)}/20` : '—'}
            </p>
          </div>
          <div className="apprenant-inscription-stat">
            <p className="apprenant-inscription-stat__label">Présence</p>
            <p className="apprenant-inscription-stat__value apprenant-inscription-stat__value--neutral">
              {stats?.tauxPresencePct != null ? `${stats.tauxPresencePct} %` : '—'}
            </p>
          </div>
        </div>
      </ApprenantSectionCard>

      {data.scoresExcellence && data.scoresExcellence.length > 0 ? (
        <ApprenantSectionCard icon="emoji_events" title="Classement" subtitle="Scores et rangs par période">
          <ApprenantPaginatedList
            items={data.scoresExcellence}
            emptyLabel="Aucun classement enregistré."
            listClassName="list-none space-y-2 p-0 text-sm"
            renderItem={(s) => (
              <li
                key={s.periode}
                className="flex justify-between rounded-lg border border-outline-variant/20 px-3 py-2"
              >
                <span>
                  {s.periode}
                  {s.estShortlist ? (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 text-[10px] font-bold text-amber-800">
                      Excellence
                    </span>
                  ) : null}
                </span>
                <span className="font-bold">
                  {s.rang != null ? `#${s.rang}` : '—'} · score {s.scoreGlobal}
                </span>
              </li>
            )}
          />
        </ApprenantSectionCard>
      ) : null}

      <ApprenantSectionCard icon="checklist" title="Notes & évaluations" subtitle="Résultats publiés par vos formateurs">
        <ApprenantPaginatedTable
          items={data.notes ?? []}
          emptyLabel="Aucune note publiée pour le moment."
          columns={[
            { header: 'Type' },
            { header: 'Libellé' },
            { header: 'Dépôt' },
            { header: 'Note', className: 'text-right' },
          ]}
          renderRow={(n, i) => (
            <tr key={`${n.typeLabel}-${n.libelle}-${i}`} className="border-t border-outline-variant/10">
              <td className="py-2">{n.typeLabel}</td>
              <td className="py-2">{n.libelle}</td>
              <td className="py-2 text-xs text-on-surface-variant">
                {n.statutDepot ?? (n.urlFichier ? 'Fichier' : '—')}
              </td>
              <td className="py-2 text-right font-bold">{n.noteSur20}/20</td>
            </tr>
          )}
        />
      </ApprenantSectionCard>

      <ApprenantSectionCard
        icon="stars"
        title="Évaluer vos formateurs"
        subtitle="Ponctualité et pédagogie (1 à 5)"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <ApprenantFormField label="Formateur" htmlFor="eval-formateur">
            <AcademiaSelect
              id="eval-formateur"
              value={evalForm.idProfilFormateur}
              onChange={(e) => setEvalForm((f) => ({ ...f, idProfilFormateur: e.target.value }))}
            >
              <option value="">Choisir un formateur</option>
              {formateurs.map((f) => (
                <option key={f.idProfilFormateur} value={f.idProfilFormateur} disabled={f.dejaEvalue}>
                  {f.nomComplet} {f.dejaEvalue ? '(déjà évalué)' : ''}
                </option>
              ))}
            </AcademiaSelect>
          </ApprenantFormField>
          <ApprenantFormField label="Ponctualité (1 à 5)" htmlFor="eval-ponctualite">
            <input
              id="eval-ponctualite"
              type="number"
              min={1}
              max={5}
              className="academia-field w-full"
              value={evalForm.notePonctualite}
              onChange={(e) => setEvalForm((f) => ({ ...f, notePonctualite: e.target.value }))}
            />
          </ApprenantFormField>
          <ApprenantFormField label="Pédagogie (1 à 5)" htmlFor="eval-pedagogie">
            <input
              id="eval-pedagogie"
              type="number"
              min={1}
              max={5}
              className="academia-field w-full"
              value={evalForm.notePedagogie}
              onChange={(e) => setEvalForm((f) => ({ ...f, notePedagogie: e.target.value }))}
            />
          </ApprenantFormField>
          <ApprenantFormField
            label="Commentaire (optionnel)"
            htmlFor="eval-commentaire"
            className="sm:col-span-2"
          >
            <textarea
              id="eval-commentaire"
              rows={2}
              className="academia-field w-full resize-y"
              value={evalForm.commentaire}
              onChange={(e) => setEvalForm((f) => ({ ...f, commentaire: e.target.value }))}
            />
          </ApprenantFormField>
        </div>
        <button
          type="button"
          disabled={submittingEval}
          className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          onClick={async () => {
            if (!evalForm.idProfilFormateur) {
              toast('Sélectionnez un formateur à évaluer.', 'error');
              return;
            }
            setSubmittingEval(true);
            try {
              await submitEvaluationFormateur(inscriptionId, {
                idProfilFormateur: evalForm.idProfilFormateur,
                notePonctualite: Number(evalForm.notePonctualite),
                notePedagogie: Number(evalForm.notePedagogie),
                commentaire: evalForm.commentaire || undefined,
              });
              toast('Merci — votre évaluation a été enregistrée.', 'success');
              setFormateurs(await fetchFormateursToEvaluate(inscriptionId));
            } catch (e) {
              toast(e instanceof Error ? e.message : "Impossible d'envoyer l'évaluation.", 'error');
            } finally {
              setSubmittingEval(false);
            }
          }}
        >
          {submittingEval ? 'Envoi…' : "Envoyer l'évaluation"}
        </button>
      </ApprenantSectionCard>

      <ApprenantSectionCard
        icon="upload"
        title="Dépôt de travaux"
        subtitle="TP, soutenance ou projet"
      >
        {data.cohorteVerrouillee ? (
          <p className="text-sm text-on-surface-variant">Dépôts fermés après délibération.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <ApprenantFormField label="Type de travail" htmlFor="depot-type">
              <AcademiaSelect
                id="depot-type"
                value={depotForm.typeEvaluation}
                onChange={(e) => setDepotForm((f) => ({ ...f, typeEvaluation: e.target.value }))}
              >
                <option value="TP">TP</option>
                <option value="SOUTENANCE">Soutenance</option>
                <option value="PROJET">Projet</option>
              </AcademiaSelect>
            </ApprenantFormField>
            <ApprenantFormField label="Libellé du travail" htmlFor="depot-libelle">
              <input
                id="depot-libelle"
                className="academia-field w-full"
                value={depotForm.libelle}
                onChange={(e) => setDepotForm((f) => ({ ...f, libelle: e.target.value }))}
              />
            </ApprenantFormField>
            <ApprenantFormField
              label="Lien du fichier"
              htmlFor="depot-url"
              hint="URL (drive, cloud…) ou téléversez un fichier ci-dessous"
              className="sm:col-span-2"
            >
              <input
                id="depot-url"
                className="academia-field w-full"
                value={depotForm.urlFichier}
                onChange={(e) => setDepotForm((f) => ({ ...f, urlFichier: e.target.value }))}
              />
            </ApprenantFormField>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.txt"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadingDepot(true);
                try {
                  const path = await uploadDepotTravailFile(file);
                  setDepotForm((f) => ({ ...f, urlFichier: path }));
                  toast('Fichier téléversé — validez le dépôt.', 'success');
                } catch (err) {
                  toast(err instanceof Error ? err.message : 'Échec du téléversement.', 'error');
                } finally {
                  setUploadingDepot(false);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }
              }}
            />
            <div className="flex flex-wrap gap-2 sm:col-span-2">
              <button
                type="button"
                disabled={uploadingDepot}
                className="rounded-lg border border-outline-variant/30 px-4 py-2 text-sm font-bold text-slate-700"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingDepot ? 'Téléversement…' : 'Choisir un fichier'}
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                disabled={!depotForm.urlFichier || submittingDepot}
                onClick={async () => {
                  setSubmittingDepot(true);
                  try {
                    await submitDepotTravail(inscriptionId, depotForm);
                    toast('Travail déposé avec succès.', 'success');
                    const row = await fetchInscriptionAcademic(inscriptionId);
                    setData(row);
                  } catch (e) {
                    toast(e instanceof Error ? e.message : 'Impossible de déposer le travail.', 'error');
                  } finally {
                    setSubmittingDepot(false);
                  }
                }}
              >
                {submittingDepot ? 'Dépôt…' : 'Déposer le travail'}
              </button>
            </div>
          </div>
        )}
      </ApprenantSectionCard>

      <ApprenantSectionCard icon="event_available" title="Présences" subtitle="Historique des séances">
        <ApprenantPaginatedList
          items={data.presences ?? []}
          emptyLabel="Aucune présence enregistrée."
          renderItem={(p, i) => (
            <li key={`${p.dateSeance}-${p.sessionLabel}-${i}`} className="apprenant-inscription-list-row">
              <span className="text-on-surface-variant">
                {new Date(p.dateSeance).toLocaleDateString('fr-FR')} — {p.sessionLabel}
              </span>
              <span className="font-semibold">{p.statut}</span>
            </li>
          )}
        />
      </ApprenantSectionCard>
    </div>
  );
}
